import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Níveis de acesso do sistema (apenas para administradores do painel)
export enum NivelAcesso {
  DESENVOLVEDOR = 0,  // The One - Acesso total ao painel
  ADMINISTRADOR = 1,  // Admin - CRUD de usuários no painel
  GERENTE = 2,        // Acesso ao painel sem CRUD de usuários
  SOCIO = 3           // Sócio comum - SEM acesso ao painel admin
}

export const NIVEL_DESCRICAO: Record<number, string> = {
  [NivelAcesso.DESENVOLVEDOR]: 'Desenvolvedor (The One)',
  [NivelAcesso.ADMINISTRADOR]: 'Administrador',
  [NivelAcesso.GERENTE]: 'Gerente',
  [NivelAcesso.SOCIO]: 'Sócio (Sem Acesso Admin)'
}

// Tipos para as tabelas
export interface Socio {
  id: string
  nome: string
  cpf: string
  telefone: string
  email: string
  placa?: string
  foto_url?: string
  status: 'ativo' | 'inadimplente' | 'inativo'
  plano: 'mensal' | 'trimestral' | 'anual'
  valor_mensalidade: number
  data_vencimento: string
  precisa_atualizar_senha?: boolean
  isento_mensalidade?: boolean
  isencao_motivo?: string
  isencao_data?: string
  isencao_concedida_por?: string
  data_nascimento?: string | null
  criado_em: string
  atualizado_em: string
}

export interface Admin {
  id: string
  user_id: string
  nome: string
  email: string
  nivel_acesso: number  // 0=Dev, 1=Admin, 2=Gerente
  criado_em: string
}

export interface EstacionamentoVaga {
  id: string
  codigo: string
  descricao: string
  ativo: boolean
  criado_em: string
}

export interface EstacionamentoEntrada {
  id: string
  socio_id: string
  vaga_id: string
  placa: string
  entrada: string
  saida?: string
  tempo_minutos?: number
  valor: number
  pago: boolean
  pagamento_id?: string
  criado_em: string
}

export interface Pagamento {
  id: string
  socio_id: string
  tipo: 'mensalidade' | 'estacionamento'
  descricao: string
  valor: number
  status: 'pendente' | 'pago' | 'cancelado'
  pagseguro_code?: string
  payload?: any
  criado_em: string
  created_at: string
}

export interface Cobranca {
  id: string
  socio_id: string
  descricao: string
  valor: number
  status: 'pendente' | 'pago' | 'cancelado'
  data_vencimento: string
  data_pagamento?: string
  pagbank_charge_id?: string
  checkout_url?: string
  qrcode_base64?: string
  payload?: any
  created_at: string
}

export interface SocioAuth {
  id: string
  user_id: string
  socio_id: string
  criado_em: string
}

// Funções auxiliares
export const getSocioByUserId = async (userId: string) => {
  const { data, error } = await supabase
    .from('socios_auth')
    .select(`
      socio_id,
      socios (*)
    `)
    .eq('user_id', userId)
    .maybeSingle()

  if (error && error.code !== 'PGRST116') {
    console.error('Erro ao buscar sócio:', error);
    throw error;
  }

  const socio = data?.socios as any;
  return socio as Socio | null;
}

export const getAdminByUserId = async (userId: string) => {
  const { data, error } = await supabase
    .from('admins')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle()

  if (error && error.code !== 'PGRST116') {
    console.error('Erro ao buscar admin:', error);
    throw error;
  }

  return data as Admin | null;
}

export const getPagamentosBySocio = async (socioId: string) => {
  const { data, error } = await supabase
    .from('pagamentos')
    .select('*')
    .eq('socio_id', socioId)
    .order('criado_em', { ascending: false })

  if (error) throw error
  return data as Pagamento[]
}

export const getCobrancasBySocio = async (socioId: string) => {
  const { data, error } = await supabase
    .from('cobrancas')
    .select('*')
    .eq('socio_id', socioId)
    .order('data_vencimento', { ascending: true })

  if (error) throw error
  return data as Cobranca[]
}

export const getEstacionamentoBySocio = async (socioId: string) => {
  const { data, error } = await supabase
    .from('estacionamento_entradas')
    .select(`
      *,
      estacionamento_vagas (codigo, descricao)
    `)
    .eq('socio_id', socioId)
    .order('entrada', { ascending: false })

  if (error) throw error
  return data
}

// ========================================
// Funções de Permissões (para Admins)
// ========================================

/**
 * Verifica se o admin tem permissão baseado no nível mínimo requerido
 * @param admin Dados do admin
 * @param nivelMinimo Nível mínimo necessário
 * @returns true se tem permissão
 */
export const temPermissao = (admin: Admin | null, nivelMinimo: NivelAcesso): boolean => {
  if (!admin) return false
  const nivel = admin.nivel_acesso ?? 3
  // Quanto MENOR o nível, MAIOR a permissão (0 > 1 > 2)
  return nivel <= nivelMinimo
}

/**
 * Verifica se é desenvolvedor (The One)
 */
export const isTheOne = (admin: Admin | null): boolean => {
  return temPermissao(admin, NivelAcesso.DESENVOLVEDOR)
}

/**
 * Verifica se é administrador (nível 0 ou 1)
 */
export const isAdmin = (admin: Admin | null): boolean => {
  if (!admin) return false
  return admin.nivel_acesso !== undefined && admin.nivel_acesso <= NivelAcesso.ADMINISTRADOR
}

/**
 * Verifica se admin tem acesso ao painel (nivel 0, 1 ou 2)
 */
export const hasPanelAccess = (admin: Admin | null): boolean => {
  if (!admin) return false
  return (admin.nivel_acesso ?? 3) <= 2
}

/**
 * Obtém descrição do nível de acesso
 */
export const getNivelDescricao = (nivel?: number): string => {
  if (nivel === undefined || nivel === null) return 'Sem Acesso'
  return NIVEL_DESCRICAO[nivel] || 'Sem Acesso'
}